import { AlertTriangle, CalendarClock } from "lucide-react";
import { prazoEfetivo, rotuloDoPrazo, type SituacaoDoPrazo } from "@bookdental/schemas";
import { cn } from "@/lib/cn";
import { usePrazoDoLaboratorio } from "@/lib/prazo-do-laboratorio";
import { formatDate } from "@/components/prosthesis/prosthesis-meta";
import { situacaoDoServico, type ServiceRow } from "./production-types";

/** Atrasado tem precedência sobre "vence em breve" (janela do aviso do laboratório). */
function useSituacao(rows: ServiceRow[]) {
  const prazo = usePrazoDoLaboratorio();
  const order = rows[0].order;
  const situacoes = rows.map((r) => situacaoDoServico(r, prazo));
  const situacao: SituacaoDoPrazo = situacoes.includes("atrasado") ? "atrasado" : situacoes.includes("venceEmBreve") ? "venceEmBreve" : "noPrazo";
  const vence = formatDate(prazoEfetivo(order, prazo));
  const motivo =
    situacao === "atrasado"
      ? order.deadline
        ? `Atrasado — prazo era ${vence}`
        : `Atrasado — sem prazo combinado, passou dos ${rotuloDoPrazo(prazo)} (${vence})`
      : `Vence em breve — ${vence}`;
  return { order, situacao, motivo };
}

const COR = {
  atrasado: "font-semibold text-red-600 dark:text-red-400",
  venceEmBreve: "font-semibold text-amber-600 dark:text-amber-400",
  noPrazo: "",
} as const;

/** Data do pedido — vermelha quando atrasado, âmbar quando vence em breve. */
export function DataComAtraso({ rows, className }: { rows: ServiceRow[]; className?: string }) {
  const { order, situacao, motivo } = useSituacao(rows);
  return (
    <span className={cn(className, COR[situacao])} title={situacao === "noPrazo" ? undefined : motivo}>
      {formatDate(order.createdAt)}
    </span>
  );
}

/** Prazo — vermelho quando atrasado, âmbar quando vence em breve; sem prazo combinado, só o ícone. */
export function PrazoComAtraso({ rows, className }: { rows: ServiceRow[]; className?: string }) {
  const { order, situacao, motivo } = useSituacao(rows);
  if (situacao === "noPrazo") return <span className={className}>{formatDate(order.deadline)}</span>;
  const Icone = situacao === "atrasado" ? AlertTriangle : CalendarClock;
  return (
    <span
      aria-label={situacao === "atrasado" ? "Atrasado" : "Vence em breve"}
      title={motivo}
      className={cn(className, "inline-flex items-center", COR[situacao])}
    >
      {order.deadline ? formatDate(order.deadline) : <Icone className="h-3.5 w-3.5" />}
    </span>
  );
}

/** Selo âmbar "Vence em breve" — o pedido entrou na janela do aviso do laboratório. */
export function SeloVenceEmBreve({ compacto = false }: { compacto?: boolean }) {
  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center gap-1 rounded-full bg-amber-100 font-bold uppercase text-amber-800 dark:bg-amber-500/15 dark:text-amber-400",
        compacto ? "px-1.5 py-0.5 text-[10px]" : "px-2.5 py-1 text-[11px]",
      )}
    >
      <CalendarClock className="h-3 w-3" />
      Vence em breve
    </span>
  );
}
